import {
  buildCareProfileOverview,
  getCareProfileOverviewCompletionSummary,
} from "./careProfileSections";

const hasAnyValue = (value) => {
  if (Array.isArray(value)) {
    return value.some((item) => {
      if (item == null) return false;
      if (typeof item === "string") return item.trim().length > 0;
      if (typeof item === "object") {
        return Object.values(item).some((nested) => {
          if (nested == null) return false;
          if (typeof nested === "string") return nested.trim().length > 0;
          return Boolean(nested);
        });
      }
      return Boolean(item);
    });
  }

  if (typeof value === "object" && value) {
    return Object.values(value).some((nested) => {
      if (nested == null) return false;
      if (typeof nested === "string") return nested.trim().length > 0;
      if (Array.isArray(nested)) return hasAnyValue(nested);
      return Boolean(nested);
    });
  }

  return typeof value === "string" ? value.trim().length > 0 : Boolean(value);
};

const hasProfilePhoto = (child = {}) => (
  hasAnyValue(child.profilePhoto)
  || hasAnyValue(child.photoURL)
  || hasAnyValue(child.avatarUrl)
);

const getMedicalProfile = (child = {}) => child.medicalProfile || {};

const hasActiveMedicationContext = (child = {}, options = {}) => {
  if (typeof options.hasActiveMedications === "boolean") {
    return options.hasActiveMedications;
  }

  if (Number.isFinite(options.activeMedicationCount)) {
    return options.activeMedicationCount > 0;
  }

  if (typeof child.careProfileHasActiveMedications === "boolean") {
    return child.careProfileHasActiveMedications;
  }

  if (Number.isFinite(child.careProfileActiveMedicationCount)) {
    return child.careProfileActiveMedicationCount > 0;
  }

  return false;
};

const getMedicalProfileForCompletion = (child = {}, options = {}) => {
  const medicalProfile = getMedicalProfile(child);
  const hasMedicationInProfile = hasAnyValue(medicalProfile.medicationRecords);

  if (hasMedicationInProfile || !hasActiveMedicationContext(child, options)) {
    return medicalProfile;
  }

  return {
    ...medicalProfile,
    medicationRecords: [{ name: "Active medication saved" }],
  };
};

const getSelectedConditions = (child = {}) => {
  const conditions = child.concerns || child.conditions || child.selectedConditions || child.careContext?.careFocus || [];
  return Array.isArray(conditions) ? conditions : [conditions].filter(Boolean);
};

export const getChildCareTeamMemberCount = (child = {}, options = {}) => {
  if (Number.isFinite(options.careTeamMemberCount)) {
    return Math.max(0, options.careTeamMemberCount);
  }

  if (Array.isArray(options.careTeamMembers)) {
    return Math.max(0, options.careTeamMembers.length - 1);
  }

  if (Number.isFinite(child.careTeamMemberCount)) {
    return Math.max(0, child.careTeamMemberCount);
  }

  const users = child.users || {};
  const roleMembers = [
    ...(Array.isArray(users.care_partners) ? users.care_partners : []),
    ...(Array.isArray(users.caregivers) ? users.caregivers : []),
    ...(Array.isArray(users.therapists) ? users.therapists : []),
  ];

  if (roleMembers.length) {
    return roleMembers.length;
  }

  const members = users.members || child.members || [];
  return Array.isArray(members) ? Math.max(0, members.length - 1) : 0;
};

const buildCompletionOverview = (child = {}, options = {}) => {
  if (options.profileOverview) {
    return options.profileOverview;
  }

  return buildCareProfileOverview({
    name: child.name,
    age: child.age,
    photoURL: child.photoURL || child.profilePhoto || child.avatarUrl || "",
    selectedConditions: getSelectedConditions(child),
    careContext: child.careContext || {},
    medicalProfile: getMedicalProfileForCompletion(child, options),
    supportProfile: child.supportProfile || child.supportGoals || {},
    careTeamMemberCount: getChildCareTeamMemberCount(child, options),
  });
};

const PROFILE_SETUP_REQUIREMENTS = [
  {
    key: "name",
    label: "Add profile name",
    pillText: "add profile name",
    isComplete: (child) => hasAnyValue(child.name),
  },
  {
    key: "age",
    label: "Add age",
    pillText: "add age",
    isComplete: (child) => hasAnyValue(child.age),
  },
  {
    key: "photo",
    label: "Add profile photo",
    pillText: "add profile photo",
    isComplete: (child) => hasProfilePhoto(child),
  },
  {
    key: "careFocus",
    label: "Add care focus",
    pillText: "add care focus",
    isComplete: (child) => hasAnyValue(child.careContext?.careFocus),
  },
  {
    key: "allergies",
    label: "Add allergy info",
    pillText: "add allergy info",
    isComplete: (child) => hasAnyValue(getMedicalProfile(child).foodAllergies),
  },
  {
    key: "medications",
    label: "Add medication details",
    pillText: "add medication details",
    isComplete: (child, options) => {
      const medicalProfile = getMedicalProfileForCompletion(child, options);
      return hasAnyValue(medicalProfile.medicationRecords);
    },
  },
  {
    key: "sensory",
    label: "Add sensory notes",
    pillText: "add sensory notes",
    isComplete: (child) => hasAnyValue(getMedicalProfile(child).sensoryIssues),
  },
  {
    key: "behavior",
    label: "Add behavior triggers",
    pillText: "add behavior triggers",
    isComplete: (child) => hasAnyValue(getMedicalProfile(child).behavioralTriggers),
  },
  {
    key: "communication",
    label: "Add communication needs",
    pillText: "add communication needs",
    isComplete: (child) => hasAnyValue(getMedicalProfile(child).communicationNeeds),
  },
  {
    key: "sleep",
    label: "Add sleep notes",
    pillText: "add sleep notes",
    isComplete: (child) => hasAnyValue(getMedicalProfile(child).sleepIssues),
  },
  {
    key: "diet",
    label: "Add dietary needs",
    pillText: "add dietary needs",
    isComplete: (child) => hasAnyValue(getMedicalProfile(child).dietaryRestrictions),
  },
];

export const getChildProfileCompletion = (child = {}, options = {}) => {
  const summary = getCareProfileOverviewCompletionSummary(buildCompletionOverview(child, options));
  return summary.percent;
};

export const getChildProfileCompletionSummary = (child = {}, options = {}) => (
  getCareProfileOverviewCompletionSummary(buildCompletionOverview(child, options))
);

export const isChildProfileComplete = (child = {}, options = {}) => (
  getChildProfileCompletion(child, options) >= 100
);

export const getChildProfileSetupItems = (child = {}, options = {}) => (
  PROFILE_SETUP_REQUIREMENTS.map(({ isComplete, ...item }) => ({
    ...item,
    completed: isComplete(child, options),
  }))
);

export const getChildProfileSetupStatus = (child = {}, options = {}) => {
  const completionSummary = getChildProfileCompletionSummary(child, options);
  const completionPercent = Math.max(0, Math.min(100, completionSummary.percent));
  const isComplete = completionSummary.total > 0
    ? completionSummary.remaining === 0
    : completionPercent >= 100;
  const remainingItems = isComplete
    ? []
    : getChildProfileSetupItems(child, options).filter((item) => !item.completed);
  const nextItem = !isComplete && remainingItems.length === 1 ? remainingItems[0] : null;

  return {
    completionPercent,
    completionSummary,
    completionLabel: completionSummary.label,
    isComplete,
    remainingItems,
    nextItem,
    shouldShowRing: !isComplete,
    shouldShowSingleItemPill: Boolean(nextItem),
  };
};
